import React from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Option, Select } from '../../../../ui/FormControls';
import FormTextField from '../../../../ui/form/FormTextField';
import ManageTemplateFormItem from '../../../../ui/ManageTemplateFormItem';
import { FormControl } from '../../../../../utils/form.utils';

interface UserGuideScopeFormFieldsProps {
    nameField: FormControl<string>;
    scopeField: FormControl<string[]>;
    duplicateNameError: string;
    neonAccessOptions: Option[];
    selectedNeonAccessOptions: Option[];
    permissionOptions: Option[];
    selectedPermissionOptions: Option[];
    scopeValuesByOptionValue: Map<string, string[]>;
}

interface ScopeSelectProps {
    id: string;
    label: string;
    options: Option[];
    selectedOptions: Option[];
    permissionSelect: boolean;
    scopeField: FormControl<string[]>;
    scopeValuesByOptionValue: Map<string, string[]>;
}

const permissionFilterOptions = createFilterOptions<Option>({
    stringify: (option) =>
        `${option.groupLabel || ''} ${option.label || ''} ${
            option.secondaryLabel || ''
        }`,
});

const ScopeSelect = ({
    id,
    label,
    options,
    selectedOptions,
    permissionSelect,
    scopeField,
    scopeValuesByOptionValue,
}: ScopeSelectProps) => (
    <ManageTemplateFormItem>
        <Select
            id={id}
            label={label}
            value={selectedOptions}
            options={options}
            placeholder={
                options.length
                    ? `Select ${label.toLowerCase()}...`
                    : `No ${label.toLowerCase()} available`
            }
            multi={true}
            clearable={false}
            groupBy={
                permissionSelect
                    ? (option: Option) => option.groupLabel || ''
                    : undefined
            }
            filterOptions={
                permissionSelect ? permissionFilterOptions : undefined
            }
            error={scopeField.showError}
            errorText={scopeField.validationMessage}
            disabled={!options.length}
            onBlur={() => scopeField.setDirty(true)}
            onChange={(opt: Option | Option[] | null) => {
                let nextSelectedOptions: Option[] = [];

                if (Array.isArray(opt)) {
                    nextSelectedOptions = opt;
                } else if (opt) {
                    nextSelectedOptions = [opt];
                }

                const selectedScopes = nextSelectedOptions.flatMap(
                    (item) =>
                        scopeValuesByOptionValue.get(item.value.toString()) ||
                        []
                );
                const scopesFromOtherSelect = scopeField.value.filter(
                    (scope) => scope.includes(':') !== permissionSelect
                );

                scopeField.setValue(
                    Array.from(
                        new Set([...scopesFromOtherSelect, ...selectedScopes])
                    )
                );
            }}
        />
    </ManageTemplateFormItem>
);

const UserGuideScopeFormFields: React.FC<UserGuideScopeFormFieldsProps> = ({
    nameField,
    scopeField,
    duplicateNameError,
    neonAccessOptions,
    selectedNeonAccessOptions,
    permissionOptions,
    selectedPermissionOptions,
    scopeValuesByOptionValue,
}) => (
    <>
        <FormTextField
            id="user-guide-scope-name-input"
            label="Name"
            field={{
                ...nameField,
                showError: nameField.showError || !!duplicateNameError,
                validationMessage:
                    duplicateNameError || nameField.validationMessage || '',
            }}
            charsLimit={100}
        />
        <ScopeSelect
            id="user-guide-scope-neon-access-input"
            label="Neon Access"
            options={neonAccessOptions}
            selectedOptions={selectedNeonAccessOptions}
            permissionSelect={false}
            scopeField={scopeField}
            scopeValuesByOptionValue={scopeValuesByOptionValue}
        />
        <ScopeSelect
            id="user-guide-scope-permissions-input"
            label="Permissions"
            options={permissionOptions}
            selectedOptions={selectedPermissionOptions}
            permissionSelect={true}
            scopeField={scopeField}
            scopeValuesByOptionValue={scopeValuesByOptionValue}
        />
    </>
);

export default UserGuideScopeFormFields;
